import { createSlice } from "@reduxjs/toolkit";
import { isNil } from "lodash-es";
import moment from "moment";
import { View, ViewTagsMap } from "../components/chart/models/TrendingModels";
import { scopeType } from "../components/chart/models/scopes.model";

const TrendingsInitialState: {
	timeScopeStart: Date;
	timeScopeEnd: Date;
	scope: scopeType;
	graphPan?: boolean;
	graphShouldUpdate: number;
	shouldRefetchSeries: number;
	views: View[];
	viewSelected: View | null;
	viewTags: ViewTagsMap;
} = {
	timeScopeStart: moment(new Date()).subtract(1, "days").toDate(),
	timeScopeEnd: new Date(),
	scope: "1 day",
	graphPan: false,
	graphShouldUpdate: 0,
	views: [
		{
			ViewId: 0,
			ViewName: "",
			UserName: null,
			IsPublic: false,
			LastModified: null,
			LastModifiedBy: null,
		},
	],
	viewSelected: null,
	viewTags: {},
	shouldRefetchSeries: 1,
};

export const TrendingsReducer = createSlice({
	name: "__",
	initialState: TrendingsInitialState,
	reducers: {
		setTotalScope(
			state,
			{
				payload,
				type,
			}: {
				payload:
					| {
							scope: scopeType;
							start?: Date;
							end?: Date;
							graphPan?: boolean;
					  }
					| {
							scope?: scopeType;
							start: Date;
							end?: Date;
							graphPan?: boolean;
					  }
					| {
							scope?: scopeType;
							start?: Date;
							end: Date;
							graphPan?: boolean;
					  }
					| {
							scope?: scopeType;
							start: Date;
							end: Date;

							graphPan: boolean;
					  };

				type: string;
			},
		) {
			state.shouldRefetchSeries += 1;
			state.graphShouldUpdate += 1;
			if (
				!isNil(payload.start) &&
				!isNil(payload.end) &&
				!isNil(payload.scope)
			) {
				//from arrow navigators
				state.timeScopeStart = payload.start;
				state.timeScopeEnd = payload.end;
				state.scope = payload.scope;
				return;
			}

			if (!isNil(payload.scope)) {
				if (payload.scope !== "custom") {
					switch (payload.scope) {
						case "10 min":
							state.timeScopeStart = moment(state.timeScopeEnd)
								.subtract(10, "minutes")
								.toDate();
							break;
						case "1 hour":
							state.timeScopeStart = moment(state.timeScopeEnd)
								.subtract(1, "hours")
								.toDate();
							break;
						case "4 hours":
							state.timeScopeStart = moment(state.timeScopeEnd)
								.subtract(4, "hours")
								.toDate();
							break;
						case "12 hours":
							state.timeScopeStart = moment(state.timeScopeEnd)
								.subtract(12, "hours")
								.toDate();
							break;
						case "1 day":
							state.timeScopeStart = moment(state.timeScopeEnd)
								.subtract(1, "days")
								.toDate();
							break;
						case "10 days":
							state.timeScopeStart = moment(state.timeScopeEnd)
								.subtract(10, "days")
								.toDate();
							break;
					}
				}
				state.scope = payload.scope;
				return;
			}

			if (
				!isNil(payload.start) &&
				!isNil(payload.end) &&
				isNil(payload.graphPan)
			) {
				// from zoom
				state.timeScopeStart = payload.start;
				state.timeScopeEnd = payload.end;
				state.scope = "custom";
				return;
			}

			if (
				!isNil(payload.start) &&
				!isNil(payload.end) &&
				!isNil(payload.graphPan)
			) {
				// from pan

				state.timeScopeStart = payload.start;
				state.timeScopeEnd = payload.end;
				return;
			}

			if (!isNil(payload.start)) {
				state.timeScopeStart = payload.start;
				if (state.scope !== "custom") {
					switch (state.scope) {
						case "10 min":
							state.timeScopeEnd = moment(payload.start)
								.add(10, "minutes")
								.toDate();
							break;
						case "1 hour":
							state.timeScopeEnd = moment(payload.start)
								.add(1, "hours")
								.toDate();
							break;
						case "4 hours":
							state.timeScopeEnd = moment(payload.start)
								.add(4, "hours")
								.toDate();
							break;
						case "12 hours":
							state.timeScopeEnd = moment(payload.start)
								.add(12, "hours")
								.toDate();
							break;
						case "1 day":
							state.timeScopeEnd = moment(payload.start)
								.add(1, "days")
								.toDate();
							break;
						case "10 days":
							state.timeScopeEnd = moment(payload.start)
								.add(10, "days")
								.toDate();
							break;
					}
				}
				return;
			}

			if (!isNil(payload.end)) {
				state.timeScopeEnd = payload.end;
				if (state.scope !== "custom") {
					switch (state.scope) {
						case "10 min":
							state.timeScopeStart = moment(payload.end)
								.subtract(10, "minutes")
								.toDate();
							break;
						case "1 hour":
							state.timeScopeStart = moment(payload.end)
								.subtract(1, "hours")
								.toDate();
							break;
						case "4 hours":
							state.timeScopeStart = moment(payload.end)
								.subtract(4, "hours")
								.toDate();
							break;
						case "12 hours":
							state.timeScopeStart = moment(payload.end)
								.subtract(12, "hours")
								.toDate();
							break;
						case "1 day":
							state.timeScopeStart = moment(payload.end)
								.subtract(1, "days")
								.toDate();
							break;
						case "10 days":
							state.timeScopeStart = moment(payload.end)
								.subtract(10, "days")
								.toDate();
							break;
					}
				}
				return;
			}
		},
		setViews(state, { payload }) {
			state.views = payload;
		},
		setViewSelected(state, { payload }) {
			state.viewSelected = payload;
		},
		setViewTags(state, { payload }) {
			state.viewTags = payload;
			state.graphShouldUpdate += 1;
		},
		setViewTagsAndRefetch(state, { payload }) {
			const newTags = structuredClone(payload);
			const newTagsSorted = [...Object.values(payload as ViewTagsMap)]
				.sort((a, b) => b.order - a.order)
				.forEach((vtMap, idx) => {
					newTags[vtMap.viewTag.TagId]["order"] = idx;
				});
			state.viewTags = newTags;
			state.shouldRefetchSeries += 1;
		},
		setGraphShouldUpdate(state) {
			state.graphShouldUpdate = +1;
		},
	},
});
